import { useSelector } from "react-redux";
import styled from "styled-components";
import ButtonArrow from "~/components/ButtonArrow";
import ButtonOutline from "~/components/ButtonOutline";
import BuyButton from "~/components/BuyButton";
import Divider from "~/components/Divider";
import LinkButton from "~/components/LinkButton";
import MediaFull from "~/components/MediaFull";
import { imageUrl } from "~/external/commercetools";
import { paragraph, vSpacing } from "~/styles/mixins";
import theme, { rem, smallerThan } from "~/styles/theme";

// components
const PanelWrapper = styled.div`
  color: ${(p) => p.theme.colors.dark};
  display: flex;
  flex-direction: column;
`;

const PanelGrid = styled.div`
  display: flex;
  flex-direction: ${(p) => (p.align === "left" ? "row-reverse" : "row")};
  margin: 0 -${(p) => p.theme.sizes.hSpacing}rem;
  > div {
    flex: 1;
    margin: 0 ${(p) => p.theme.sizes.hSpacing}rem;
  }

  ${(p) => smallerThan(p.theme.breakpoints.ipadPortrait)} {
    flex-direction: column-reverse;
  }

  ${(p) => p.stack && `flex-direction: column-reverse`};
`;

const PanelContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.align === "right" ? "flex-end" : "flex-start")};
  flex: 1;
  color: ${(p) => p.color || p.theme.colors.dark};
`;

const PanelTitles = styled.div`
  padding-top: ${(p) => p.theme.sizes.vSpacing}rem;
  line-height: 1.6;

  width: ${(p) => (p.full ? "100%" : "50%")};
  ${(p) => smallerThan(p.theme.breakpoints.ipadPortrait)} {
    width: 100%;
  }
  flex: ${(p) => (p.stack ? "initial" : "1")};
  ${(p) => smallerThan(p.theme.breakpoints.ipadPortrait)} {
    flex: initial;
  }
`;

const Title = styled.div`
  font-size: ${rem("16px")};
  font-weight: bold;
`;

const Subtitle = styled.div`
  font-size: ${rem("16px")};
`;

const PanelText = styled.div`
  ${vSpacing};
  width: ${(p) => (p.full ? "100%" : "50%")};
  ${(p) => smallerThan(p.theme.breakpoints.ipadPortrait)} {
    width: 100%;
  }
  flex: ${(p) => (p.stack ? "1" : "initial")};
  ${(p) => smallerThan(p.theme.breakpoints.ipadPortrait)} {
    flex: initial;
  }
  p {
    ${paragraph};
    margin: ${(p) => p.theme.sizes.vSpacing}rem 0;
    max-width: ${(p) => p.maxWidth || "none"};
  }
`;

const PanelButton = styled.div`
  ${vSpacing};
  width: 50%;
  ${(p) => smallerThan(p.theme.breakpoints.ipadPortrait)} {
    width: 100%;
  }
`;

function PanelA(props) {
  let {
    stack = false,
    media,
    color,
    title,
    subtitle,
    text,
    product,
    buttonText,
    href,
    hrefAs,
    hrefTarget,
    hasTopDivider = true,
    fullWidthText = false,
    alignImage = "left",
    textAlign = "left",
    buttonType = "outline",
    buttonAction = "link",
    variantKey,
    allClickable = true,
    showProductPrice = true,
    disableImageLink = false,
  } = props;

  const countryData = useSelector((s) => s.countryData);
  // console.log('fullWidthText', fullWidthText);

  if (hrefAs === undefined) hrefAs = href;

  // disable links if buttonType === 'hidden!
  if (buttonType === "hidden") {
    allClickable = false;
  }

  // find the variantIndex! either use master, or variantKey if provided
  let variantIndex = -1;
  if (variantKey)
    variantIndex = product.variants.findIndex((v) => v.key === variantKey);
  if (variantIndex === -1) {
    variantIndex = product ? product.masterVariantIndex : 0;
    // make sure variantKey is undefined.. just in case someone wrote wrong keu
    variantKey = undefined;
  }

  // extract the variant!
  const variant = product ? product.variants[variantIndex] : null;

  // get title from variant
  if (!title && product) {
    title = product.name;
  }

  // get text from variant
  if (!text && product) {
    text = <p>{product.description}</p>;
  }

  // get image from variant
  if (!media && variant) {
    if (variant.images.length > 0) {
      if (variant.images[0].url) {
        media = [
          imageUrl(variant.images[0].url, "main"),
          "image",
          variant.images[0].dimensions.width,
          variant.images[0].dimensions.height,
        ];
      }
    }
  }

  // set default buttonText
  if (!buttonText) {
    buttonText = "Read more";

    if (variant) {
      // Disable HK warehouse shipping for now
      if (countryData.channelKey === "hk" && !variant.bundle) {
        buttonText = `Learn more`;
        showProductPrice = false;
      } else {
        buttonText = "Shop now"; // default for product
        if (buttonAction === "buy") buttonText = "Add to cart";
      }
    }
  }

  let buttonComponent = null; // aka. hidden
  switch (buttonType) {
    case "outlined":
      buttonComponent = ButtonOutline;
      break;
    case "arrow":
      buttonComponent = ButtonArrow;
      break;
  }

  /*

  // console.log('PR', props);

  // we wants our text as an array, so we can make a paragraph for each
  // in case the user just provides a string, we want to put it into an array
  // const textArr = Array.isArray(text) ? text : [text];

  const variant = product ? product.variants[product.masterVariantIndex] : null;


  // buyButtonProps will be {} if variant is not truthy
  // const buyButtonProps = useBuyButton(variant);
  // const buttonLinkProps = useLinkButton(buttonHref, buttonAs);
  // const buttonActionType = useButtonActionType(props);


  const buttonComponent = buttonType === 'arrow' ? ButtonArrow : ButtonOutline;

  // get default from card, if they are not defined
  if (!title && product) {
    title = product.name;
  }

  if (!text && product) {
    text = <p>{product.description}</p>;
  }

  if (!media && variant) {
    if (variant.images.length > 0) {
      if (variant.images[0].url) {
        media = [
          imageUrl(variant.images[0].url, 'large'),
          'image',
          variant.images[0].dimensions.width,
          variant.images[0].dimensions.height
        ];
      }
    }
  }
  */

  return (
    <PanelWrapper>
      {hasTopDivider && <Divider color={color} />}
      <PanelGrid stack={stack} align={alignImage}>
        <PanelContent color={color} align={textAlign}>
          <PanelTitles full={fullWidthText} stack={stack}>
            {title && <Title>{title}</Title>}
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </PanelTitles>
          {text && (
            <PanelText full={fullWidthText} stack={stack}>
              {text}
            </PanelText>
          )}

          <PanelButton>
            {buttonComponent && buttonAction === "link" && href && (
              <LinkButton
                buttonComponent={buttonComponent}
                buttonProps={{ color }}
                href={href}
                as={hrefAs}
                target={hrefTarget}
                buttonText={buttonText}
              />
            )}

            {buttonComponent && buttonAction === "buy" && (
              <BuyButton
                // product={product}
                variant={variant}
                buttonProps={{ color }}
                buttonComponent={buttonComponent}
                customButtonText={buttonText}
                includePrice={showProductPrice}
              />
            )}
          </PanelButton>
        </PanelContent>
        <MediaFull
          media={media}
          backgroundColor={theme.colors.greyLighter}
          clickable={allClickable && !disableImageLink}
          href={href}
          as={hrefAs}
          variant={variant}
          target={hrefTarget}
        />
      </PanelGrid>
    </PanelWrapper>
  );
}

export default PanelA;
